import type { $TSFixMe } from '@readme/iso';
import type Oas from 'oas';
import type { Operation } from 'oas/operation';

import { HTTP_METHOD } from '@readme/iso';
import React, { useMemo } from 'react';

import useClassy from '@core/hooks/useClassy';
import { useProjectStore } from '@core/store';

// eslint-disable-next-line no-restricted-imports
import AIDropdown from '@routes/SuperHub/components/AIDropdown';

import Method from '@ui/API/Method';
import RDMD from '@ui/RDMD';
import Title from '@ui/Title';

import classes from './style.module.scss';
import './style.scss';
import TagTooltip from './TagTooltip';

interface APIHeaderProps {
  badgeStyle?: 'classic' | 'modern';
  className?: string;
  doc: {
    deprecated?: boolean;
    excerpt?: string;
    slug: string;
    title: string;
    type?: string;
  };
  isWebhook?: boolean;
  markdownContent?: string;
  oas?: Oas;
  operation?: Operation;
  servers?: {
    selected?: number;
    variables?: $TSFixMe; // @todo export the `Variables` type in `oas`
  };
}

/**
 * @param {string} host
 * @param {Operation} operation
 */
function createHeaderUrl(host: string, operation: Operation, isWebhook: boolean) {
  const operationId = operation.getOperationId();

  // Split up the string based on the curly brace
  let pathParts: React.ReactNode[] | string[] = operation.path.split(/\{|\}/g);

  if (pathParts.length > 1) {
    const finalPathParts: React.ReactNode[] = [];

    pathParts.forEach((param, index) => {
      // When we split off the curly brace the even elements are the param names
      if (index % 2) {
        finalPathParts.push(
          <label
            key={`path-${operationId}_${param}-${index}`}
            className={classes['APIHeader-url-parameter']}
            htmlFor={`path-${operationId}_${param}`}
          >
            {`{${param}}`}
          </label>,
        );
      } else {
        // And the odd elements are the string literals
        finalPathParts.push(param);
      }
    });

    pathParts = finalPathParts;
  }

  return (
    <span
      className={classes['headline-container-article-info-url']}
      data-testid="serverurl"
      title={`${host}${operation.path}`}
    >
      {!isWebhook && host}
      {pathParts}
    </span>
  );
}

const APIHeader = React.memo(function APIHeader({
  doc,
  isWebhook,
  oas,
  servers,
  operation,
  markdownContent,
  badgeStyle = 'classic',
}: APIHeaderProps) {
  const opts = useMemo(() => ({ copyButtons: true }), []);
  const bem = useClassy(classes, 'headline-container');

  const [isRmdx, showAIDropdown] = useProjectStore(s => [
    s.data.features.mdx === 'enabled',
    s.data.appearance.ai?.dropdown === 'enabled',
  ]);

  // filter operation tags so we only use tags with at least 2 pieces of metadata
  const tags =
    operation?.getTags().filter(tag => {
      return tag.name && (tag.description || tag.externalDocs);
    }) || [];

  return (
    <>
      <header
        className={bem('&', doc.type === 'basic' && '_basic')}
        data-raycast-oas={doc.type === 'endpoint' && (oas?.api?._id as string)}
      >
        <div style={{ position: 'absolute', top: '0px', right: '0px', zIndex: 50 }}>
          <AIDropdown doc={doc} markdownContent={markdownContent} operation={operation} />
        </div>
        <div className={bem('-grid-item', 'button-container')}>
          <div id="content-head">
            <Title className={showAIDropdown ? classes['ai-heading-padding'] : undefined} level={1}>
              {doc.title}
              {tags?.length > 0 && <TagTooltip pageId={doc.slug} tags={tags} />}
            </Title>
          </div>
        </div>
        {(doc.type === 'endpoint' || doc.type === 'webhook') && !!oas?.api?._id && !!operation && (
          <div className={bem('-article-info')}>
            <Method
              className={bem('-api-method')}
              size="lg"
              style={badgeStyle}
              type={doc.type === 'webhook' ? HTTP_METHOD.WEBHOOK : (operation.method as HTTP_METHOD)}
            />{' '}
            {!!(operation?.isDeprecated() || doc.deprecated) && (
              <Method className={bem('-api-method')} size="lg" style={badgeStyle} type={HTTP_METHOD.DEPRECATED} />
            )}
            {createHeaderUrl(oas.url(servers?.selected, {}), operation, !!isWebhook)}
          </div>
        )}
        {!!doc.excerpt && (
          <RDMD
            body={doc.excerpt}
            className={bem('grid-item', 'excerpt')}
            opts={opts}
            renderer={isRmdx ? 'rmdx' : 'rdmd'}
          />
        )}
      </header>
    </>
  );
});

export default APIHeader;
